import { supabase } from '../lib/supabase';

/**
 * User Management Service
 * Handles all CRUD operations for user_profiles table with proper case conversion
 * Database: snake_case → React: camelCase
 */

export const userService = {
  /**
   * Fetch all users with optional filtering
   * Converts snake_case database fields to camelCase for React
   */
  async getAll(filters = {}) {
    try {
      let query = supabase?.from('user_profiles')?.select('*')?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.role && filters?.role !== 'all') {
        query = query?.eq('role', filters?.role);
      }

      if (filters?.status && filters?.status !== 'all') {
        const isActive = filters?.status === 'active';
        query = query?.eq('is_active', isActive);
      }

      if (filters?.search) {
        query = query?.or(
          `full_name.ilike.%${filters?.search}%,email.ilike.%${filters?.search}%`
        );
      }

      const { data, error } = await query;

      if (error) throw error;

      // Convert to camelCase for React
      return data?.map(user => ({
        id: user?.id,
        name: user?.full_name,
        email: user?.email,
        role: user?.role,
        status: user?.is_active ? 'active' : 'inactive',
        level: user?.level,
        xpPoints: user?.xp_points,
        inviteCode: user?.invite_code,
        profilePhotoUrl: user?.profile_photo_url,
        lastLogin: user?.updated_at, // Using updated_at as proxy for last login
        registeredDate: user?.created_at,
        createdAt: user?.created_at,
        updatedAt: user?.updated_at,
        isVoiceActor: user?.is_voice_actor || false
      }));
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  /**
   * Create new user
   * Converts camelCase to snake_case for database
   */
  async create(userData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Convert camelCase to snake_case for database
      const insertData = {
        id: crypto.randomUUID(), // Generate UUID for id column
        email: userData?.email,
        full_name: userData?.name,
        role: userData?.role || 'student',
        is_active: true,
        invite_code: userData?.inviteCode || null
      };

      if (userData?.profilePhotoUrl) {
        insertData.profile_photo_url = userData?.profilePhotoUrl;
      }

      const { data, error } = await supabase?.from('user_profiles')?.insert(insertData)?.select()?.single();

      if (error) throw error;

      // Convert back to camelCase
      return {
        id: data?.id,
        name: data?.full_name,
        email: data?.email,
        role: data?.role,
        status: data?.is_active ? 'active' : 'inactive',
        level: data?.level,
        xpPoints: data?.xp_points,
        inviteCode: data?.invite_code,
        profilePhotoUrl: data?.profile_photo_url,
        registeredDate: data?.created_at,
        lastLogin: data?.updated_at,
        isVoiceActor: data?.is_voice_actor || false
      };
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  },

  /**
   * Update existing user
   * Converts camelCase to snake_case for database
   */
  async update(userId, userData) {
    try {
      const updates = {};
      
      if (userData?.name !== undefined) updates.full_name = userData?.name;
      if (userData?.email !== undefined) updates.email = userData?.email;
      if (userData?.role !== undefined) updates.role = userData?.role;
      if (userData?.status !== undefined) {
        updates.is_active = userData?.status === 'active';
      }
      if (userData?.inviteCode !== undefined) updates.invite_code = userData?.inviteCode;
      if (userData?.profilePhotoUrl !== undefined) updates.profile_photo_url = userData?.profilePhotoUrl;

      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', userId)?.select()?.single();

      if (error) throw error;

      // Convert back to camelCase
      return {
        id: data?.id,
        name: data?.full_name,
        email: data?.email,
        role: data?.role,
        status: data?.is_active ? 'active' : 'inactive',
        level: data?.level,
        xpPoints: data?.xp_points,
        inviteCode: data?.invite_code,
        profilePhotoUrl: data?.profile_photo_url,
        registeredDate: data?.created_at,
        lastLogin: data?.updated_at,
        isVoiceActor: data?.is_voice_actor || false
      };
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  },

  /**
   * Delete user (soft delete by setting is_active to false)
   */
  async delete(userId) {
    try {
      const { error } = await supabase?.from('user_profiles')?.update({ is_active: false })?.eq('id', userId);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  },

  /**
   * Toggle user active status
   */
  async toggleStatus(userId, newStatus) {
    try {
      const isActive = newStatus === 'active';
      const { data, error } = await supabase?.from('user_profiles')?.update({ is_active: isActive })?.eq('id', userId)?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        status: data?.is_active ? 'active' : 'inactive'
      };
    } catch (error) {
      console.error('Error toggling user status:', error);
      throw error;
    }
  },

  /**
   * Toggle user voice actor status
   */
  async toggleVoiceActorStatus(userId, isVoiceActor) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update({ is_voice_actor: isVoiceActor })?.eq('id', userId)?.select()?.single();

      if (error) throw error;

      return {
        id: data?.id,
        isVoiceActor: data?.is_voice_actor || false
      };
    } catch (error) {
      console.error('Error toggling voice actor status:', error);
      throw error;
    }
  },

  /**
   * Get user statistics
   */
  async getStats() {
    try {
      const { data: allUsers, error: allError } = await supabase?.from('user_profiles')?.select('id, role, is_active, created_at');

      if (allError) throw allError;

      const now = new Date();
      const oneMonthAgo = new Date(now?.getFullYear(), now?.getMonth() - 1, now?.getDate());

      const stats = {
        totalUsers: allUsers?.length,
        newUsersThisMonth: allUsers?.filter(u => 
          new Date(u?.created_at) >= oneMonthAgo
        )?.length,
        activeStudents: allUsers?.filter(u => 
          u?.role === 'student' && u?.is_active
        )?.length,
        totalAdmins: allUsers?.filter(u => 
          u?.role === 'admin' || u?.role === 'superadmin'
        )?.length,
        adminCount: allUsers?.filter(u => u?.role === 'admin')?.length,
        inactiveUsers: allUsers?.filter(u => !u?.is_active)?.length
      };

      stats.studentEngagementRate = stats?.activeStudents > 0 
        ? Math.round((stats?.activeStudents / allUsers?.filter(u => u?.role === 'student')?.length) * 100)
        : 0;

      stats.inactivePercentage = stats?.totalUsers > 0
        ? Math.round((stats?.inactiveUsers / stats?.totalUsers) * 100)
        : 0;

      return stats;
    } catch (error) {
      console.error('Error fetching user stats:', error);
      throw error;
    }
  },

  /**
   * Bulk update user status
   */
  async bulkUpdateStatus(userIds, newStatus) {
    try {
      const isActive = newStatus === 'active';
      const { error } = await supabase?.from('user_profiles')?.update({ is_active: isActive })?.in('id', userIds);

      if (error) throw error;

      return { success: true, count: userIds?.length };
    } catch (error) {
      console.error('Error bulk updating users:', error);
      throw error;
    }
  },

  /**
   * Bulk delete users (soft delete)
   */
  async bulkDelete(userIds) {
    try {
      const { error } = await supabase?.from('user_profiles')?.update({ is_active: false })?.in('id', userIds);

      if (error) throw error;

      return { success: true, count: userIds?.length };
    } catch (error) {
      console.error('Error bulk deleting users:', error);
      throw error;
    }
  }
};